import { useQueryNormalizer } from '@normy/react-query';
import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import type { CustomHSL, HSL } from '@/features/styles/types';
import { gqlClient, queryClient } from '@/lib';
import {
  AttributionOptions,
  BannerOptions,
  type BlockLayouts,
  type ColorTypes,
  type EventCardLayouts,
  type EventTextSizes,
  type EventListDisplay,
  type ExperienceLayouts,
  type ExperienceStyles,
  type ExperienceTimeFormat,
  type HeaderTitleSizes,
  HeroOptions,
  LogoOptions,
  type PrintableConfigInput,
  type TimeZoneDisplay,
  type UpdateExperienceInput,
} from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import { usePageExperienceContext } from '../contexts/PageExperienceContext';
import { PageExperienceFragment } from '../fragments/PageExperience';
import type { PageExperience } from '../types';
import type { GetPageExperienceQuery } from './useGetPageExperience.generated';
import type {
  UpdateExperienceMutation,
  UpdateExperienceMutationVariables,
} from './useUpdateExperience.generated';

// `experienceId` overrides the context-scoped target so a caller outside the
// child's PageExperienceContext (e.g. the customize top bar) can update a
// specific experience directly. `invalidateCollection` refetches events after.
type UpdateExperienceOptions = MutateOptions<
  UpdateExperienceMutation,
  QueryError,
  UpdateExperienceMutationVariables
> & { invalidateCollection?: boolean; experienceId?: string };

const query = gql`
  ${PageExperienceFragment}
  mutation UpdateExperience($input: UpdateExperienceInput!) {
    updateExperience(input: $input) {
      ...PageExperience
    }
  }
`;

export const useUpdateExperience = () => {
  const { experience, parentExperience } = usePageExperienceContext();
  const queryNormalizer = useQueryNormalizer();
  const { revalidate } = useCollection();

  const { mutate } = useMutation<
    UpdateExperienceMutation,
    QueryError,
    UpdateExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        UpdateExperienceMutation,
        UpdateExperienceMutationVariables
      >(query, variables),
    onMutate: (variables) => {
      const experienceId = variables.input.experienceId;
      const prevValue =
        queryNormalizer.getObjectById<PageExperience>(experienceId);

      // Updating a block requires special handling
      if ('blocks' in variables.input && prevValue) {
        return {
          rollbackData: prevValue,
          optimisticData: {
            ...variables.input,
            id: variables.input.experienceId,
            blocks: prevValue?.blocks.map((block) => {
              const updatedBlock = variables.input.blocks?.find(
                (b) => b.blockId === block.id
              );
              if (block.id === updatedBlock?.blockId) {
                return {
                  ...block,
                  ...updatedBlock,
                };
              }
              return block;
            }),
          },
        };
      }

      // Printable toggles are written via the printableConfig JSON but read
      // from first-class columns (the server mirrors JSON -> columns). Mirror
      // that here so the column-reading preview updates optimistically rather
      // than lagging a round-trip. Client-cache only — the server still
      // receives printableConfig, avoiding the layout capability gating that
      // would clamp emoji/location columns for printable layouts.
      const { printableConfig } = variables.input;
      const printableToggleColumns = printableConfig
        ? {
            eventCardEmojiEnabled: printableConfig.isEmojiEnabled,
            eventCardEndTimeEnabled: printableConfig.isEndTimeEnabled,
            eventCardStartTimeEnabled: printableConfig.isStartTimeEnabled,
            eventCardLocationEnabled: printableConfig.isLocationEnabled,
            eventCardYearEnabled: printableConfig.isYearEnabled,
          }
        : {};

      return {
        rollbackData: prevValue,
        optimisticData: {
          id: variables.input.experienceId,
          ...variables.input,
          ...printableToggleColumns,
        },
      };
    },
  });

  const updateExperience = useCallback(
    (
      props: Omit<UpdateExperienceInput, 'experienceId'>,
      {
        invalidateCollection,
        experienceId,
        ...options
      }: UpdateExperienceOptions = {}
    ) => {
      const input: UpdateExperienceInput = {
        experienceId: experienceId ?? experience!.id,
        ...props,
      };

      return mutate(
        { input },
        {
          ...options,
          onSuccess: (...args) => {
            if (invalidateCollection) {
              revalidate();
            }
            // When editing a child experience, also invalidate the parent so
            // its children[] (title, layoutId) stays in sync with the sidebar.
            if (parentExperience) {
              queryClient.invalidateQueries({
                queryKey: createExperienceQueryKey(parentExperience.id),
              });
            }
            options.onSuccess?.(...args);
          },
        }
      );
    },
    [mutate, experience, parentExperience, revalidate]
  );

  const updateLocalExperience = (
    props: Omit<Partial<GetPageExperienceQuery['getExperience']>, 'id'>
  ) => {
    queryClient.setQueryData<GetPageExperienceQuery>(
      createExperienceQueryKey(experience.id),
      (prevValue) => {
        if (!prevValue) {
          return prevValue;
        }
        return {
          getExperience: {
            ...prevValue.getExperience,
            ...props,
          },
        };
      }
    );
  };

  const toggleEventCardEmojiEnabled = () => {
    updateExperience({
      eventCardEmojiEnabled: !experience.eventCardEmojiEnabled,
    });
  };

  const toggleEventCardDateEnabled = () => {
    updateExperience({
      eventCardDateEnabled: !experience.eventCardDateEnabled,
    });
  };

  const toggleEventCardEndTimeEnabled = () => {
    updateExperience({
      eventCardEndTimeEnabled: !experience.eventCardEndTimeEnabled,
    });
  };

  const toggleEventCardDescriptionEnabled = () => {
    updateExperience({
      eventCardDescriptionEnabled: !experience.eventCardDescriptionEnabled,
    });
  };

  const toggleEventCardLocationEnabled = () => {
    updateExperience({
      eventCardLocationEnabled: !experience.eventCardLocationEnabled,
    });
  };

  // Per-view planner display toggles. Persisted on the current view so they
  // drive interactive and printable output identically.
  const toggleEventCardCategoryEnabled = () => {
    updateExperience({
      eventCardCategoryEnabled: !experience.eventCardCategoryEnabled,
    });
  };

  const toggleEventCardLabelsEnabled = () => {
    updateExperience({
      eventCardLabelsEnabled: !experience.eventCardLabelsEnabled,
    });
  };

  const toggleEventCardTruncateNamesEnabled = () => {
    updateExperience({
      eventCardTruncateNamesEnabled: !experience.eventCardTruncateNamesEnabled,
    });
  };

  const toggleColorLegendEnabled = () => {
    updateExperience({
      colorLegendEnabled: !experience.colorLegendEnabled,
    });
  };

  const toggleShadeWeekendsEnabled = () => {
    updateExperience({
      shadeWeekendsEnabled: !experience.shadeWeekendsEnabled,
    });
  };

  const toggleMuteWeekendDatesEnabled = () => {
    updateExperience({
      muteWeekendDatesEnabled: !experience.muteWeekendDatesEnabled,
    });
  };

  const toggleBannerDisplay = () => {
    const visibleOption = experience.layout.bannerOptions.find(
      (item) => item !== BannerOptions.None
    );

    updateExperience({
      bannerDisplay:
        experience.bannerDisplay === BannerOptions.None
          ? visibleOption
          : BannerOptions.None,
    });
  };

  const toggleHeroDisplay = () => {
    updateExperience({
      heroDisplay:
        experience.heroDisplay === HeroOptions.None
          ? HeroOptions.RatioIntrinsic
          : HeroOptions.None,
    });
  };

  const toggleLogoDisplay = () => {
    updateExperience({
      logoDisplay:
        experience.logoDisplay === LogoOptions.None
          ? LogoOptions.Square
          : LogoOptions.None,
    });
  };

  const toggleAttributionDisplay = () => {
    updateExperience({
      attributionDisplay:
        experience.attributionDisplay === AttributionOptions.AvatarFullName
          ? AttributionOptions.None
          : AttributionOptions.AvatarFullName,
    });
  };

  const toggleSubscribeLink = () => {
    updateExperience({
      subscribeEnabled: !experience.subscribeEnabled,
    });
  };

  const toggleDescriptionEnabled = () => {
    updateExperience({
      descriptionEnabled: !experience.descriptionEnabled,
    });
  };

  const setTitle = (title: string) => {
    updateExperience({ title });
  };

  // Header title size is a per-view header setting (each planner child has its
  // own header), so it updates the current experience — no parent cascade.
  const setHeaderTitleSize = (headerTitleSize: HeaderTitleSizes) => {
    updateExperience({ headerTitleSize });
  };

  const setDescription = (description: string) => {
    updateExperience({ description });
  };

  const setPageLayout = (
    layoutId: ExperienceLayouts,
    options?: UpdateExperienceOptions
  ) => {
    updateExperience({ layoutId }, { invalidateCollection: true, ...options });
  };

  const setBlockLayout = (blockId: string, layoutId: BlockLayouts) => {
    const block = experience.blocks.find((item) => item.id === blockId);
    if (block && block.layoutId !== layoutId) {
      updateExperience({
        blocks: [
          {
            blockId,
            layoutId,
          },
        ],
      });
    }
  };

  const setEventCardLayout = (eventCardLayoutId: EventCardLayouts) => {
    updateExperience({ eventCardLayoutId });
  };

  // Planner grid text size (Event display → Default/Large); persisted per view.
  const setEventTextSize = (eventTextSize: EventTextSizes) => {
    updateExperience({ eventTextSize });
  };

  const setBoldness = (bold: number, persist?: boolean) => {
    if (persist) {
      updateExperience({ bold });
    } else {
      updateLocalExperience({ bold });
    }
  };

  const setSparkle = (sparkle: number, persist?: boolean) => {
    if (persist) {
      updateExperience({ sparkle });
    } else {
      updateLocalExperience({ sparkle });
    }
  };

  const setStyle = ({
    style,
    styleId,
    color,
    colorType,
    persist,
  }: {
    style: ExperienceStyles;
    styleId: string;
    color: HSL | CustomHSL;
    colorType: ColorTypes;
    persist?: boolean;
  }) => {
    if (persist) {
      updateExperience({
        style,
        styleId,
        ...color,
        colorType,
      });
    } else {
      updateLocalExperience({
        style,
        styleId,
        ...color,
        colorType,
      });
    }
  };

  const setColorType = (colorType: ColorTypes, persist?: boolean) => {
    if (persist) {
      updateExperience({ colorType });
    } else {
      updateLocalExperience({ colorType });
    }
  };

  const setTimezone = (timeZone: string) => {
    updateExperience({ timeZone }, { invalidateCollection: true });
  };

  const setTimezoneDisplay = (timeZoneDisplay: TimeZoneDisplay) => {
    updateExperience({ timeZoneDisplay }, { invalidateCollection: true });
  };

  const setEventListDisplay = (eventListDisplay: EventListDisplay) => {
    updateExperience({ eventListDisplay }, { invalidateCollection: true });
  };

  const setEventListDateRange = (
    eventListStartDate: string | null,
    eventListEndDate: string | null
  ) => {
    updateExperience(
      { eventListStartDate, eventListEndDate },
      { invalidateCollection: true }
    );
  };

  // Display and dates in one mutation, so switching "What to show" modes can
  // clear the dates the previous mode left behind without a second round trip
  // (and without a frame where the two disagree).
  const setEventListWindow = (
    eventListDisplay: EventListDisplay,
    eventListStartDate: string | null = null,
    eventListEndDate: string | null = null
  ) => {
    updateExperience(
      { eventListDisplay, eventListStartDate, eventListEndDate },
      { invalidateCollection: true }
    );
  };

  const setPrintableConfig = (printableConfig: PrintableConfigInput) => {
    updateExperience({ printableConfig });
  };

  const setTimeFormat = (timeFormat: ExperienceTimeFormat) => {
    updateExperience({ timeFormat });
  };

  const setWeekStartDay = (weekStartDay: number) => {
    updateExperience({ weekStartDay });
  };

  const toggleDatesInPrimaryColor = () => {
    updateExperience({
      datesInPrimaryColor: !experience.datesInPrimaryColor,
    });
  };

  const toggleHighlightToday = () => {
    updateExperience({
      highlightToday: !experience.highlightToday,
    });
  };

  return {
    updateExperience,
    toggleEventCardEmojiEnabled,
    toggleEventCardDateEnabled,
    toggleEventCardEndTimeEnabled,
    toggleEventCardDescriptionEnabled,
    toggleEventCardLocationEnabled,
    toggleEventCardCategoryEnabled,
    toggleEventCardLabelsEnabled,
    toggleEventCardTruncateNamesEnabled,
    toggleColorLegendEnabled,
    toggleShadeWeekendsEnabled,
    toggleMuteWeekendDatesEnabled,
    toggleBannerDisplay,
    toggleHeroDisplay,
    toggleLogoDisplay,
    toggleDescriptionEnabled,
    toggleAttributionDisplay,
    toggleSubscribeLink,
    setTitle,
    setHeaderTitleSize,
    setDescription,
    setPageLayout,
    setBlockLayout,
    setEventCardLayout,
    setEventTextSize,
    setBoldness,
    setSparkle,
    setColorType,
    setStyle,
    setTimezone,
    setTimezoneDisplay,
    setEventListDisplay,
    setEventListDateRange,
    setEventListWindow,
    setPrintableConfig,
    setTimeFormat,
    setWeekStartDay,
    toggleDatesInPrimaryColor,
    toggleHighlightToday,
  };
};
